/* eslint-disable guard-for-in */
/*
 * 项目业务级功能方法汇总文件
 * 通用性公共方法记得迭代到 @lhb/func 方法库
 */
import CryptoJS from 'crypto-js';
import { deepCopy, floorKeep, isArray, isNotEmpty, isNotEmptyAny, matchQuery, parseArrayToString } from '@lhb/func';
// import { getVersion } from '@lhb/bridge';
import { isAppIOS } from './tools';
import { locationImageDomain } from '@/common/enums/qiniu';
import { bridgeLogout, getAfferentToken, getAppVersion, jumpWechatService } from './bridge';
import * as dd from 'dingtalk-jsapi';
import { routePage } from '@lhb/bridge';
import { dispatchNavigate } from '../document-event/dispatch';
import { getCookie, getStorage, setCookie } from '@lhb/cache';
import { jwtDecode } from 'jwt-decode';
import { logout } from '../api/auth';
import { Toast } from 'antd-mobile';
import { SERVICE_TEL } from './contact';
import { get, isOss } from '../components/config-v2';
import { buildLoginPath } from './login-callback';

/*
 *判断数据类型
 */
export const getObjectType = (obj: any) => {
  let type = '';
  switch (Object.prototype.toString.call(obj)) {
    case '[object Function]':
      type = 'Function';
      break;
    case '[object Array]':
      type = 'Array';
      break;
    case '[object String]':
      type = 'String';
      break;
    case '[object Number]':
      type = 'Number';
      break;
    case '[object Boolean]':
      type = 'Boolean';
      break;
    default:
      type = 'Object';
  }

  return type;
};

/**
 * @param {Object} obj  要删除属性的目标对象
 * @param {Boolean} isRecursion  是否处于递归状态
 * 删除对象的属性值为null或undefined或者''的属性，无论嵌套多深，当前函数只适用请求接口时的传参的过滤
 */
export function delObjEmptyProVal(obj: any, isRecursion = false) {
  let result;
  if (isRecursion) {
    result = obj;
  } else {
    result = deepCopy(obj); // 避免vue中绑定的data
  }
  for (const item in result) {
    const val = result[item]; // 值
    if (val === null || val === undefined || val === '') {
      Reflect.deleteProperty(result, item);
    } else if (getObjectType(val) === 'Object') {
      delObjEmptyProVal(val, true);
    }
  }
  return result;
}

/**
 * 获取根元素的font-size
 * @returns {Number} 根文件的font-size大小
 */
export function getRootFontSize() {
  const doc = document.documentElement;
  const fontSize = doc.style.fontSize || window.getComputedStyle(doc).fontSize;
  return parseFloat(fontSize);
}

/**
 * 将px转为rem
 * @returns {String} rem值
 */
export function pxToRem(pxVal: number) {
  return `${pxVal / getRootFontSize()}rem`;
}


/**
 * 计算指定元素占满剩余空间所需的高度
 * @returns string | number
 */
export function calculateFreeSpaceHeight(targetEl: any, isRem = false) {
  if (!targetEl) return;
  // 移动端应该使用window.screen.height（包含了状态栏等的高度）
  const screenHeight = window.screen.height; // document.documentElement.clientHeight || document.body.clientHeight;
  const { top } = targetEl.getBoundingClientRect();
  const height = screenHeight - top;

  if (isRem) {
    return `${pxToRem(height)}`;
  }
  return height;
}

/**
 * 计算图表类
 */
export function echartsFormatUnit(data) { // data | Array
  const minVal = Math.min.apply(Math, data); // 最小值
  const unit = 10000;
  if ((minVal / unit) >= 1) { // 以万为单位
    return {
      name: 'w',
      value: 10000
    };
  } else {
    return {
      name: '',
      value: 1
    };
  }
}

/**
 * 清除ios机型下高德地图的iframe带来的router返回上一步的问题
 */
export function eliminateIframeEffects(el: Element) {
  if (isAppIOS()) {
    const iframe: any = el?.querySelector('iframe');
    iframe && iframe.parentNode && iframe.parentNode.removeChild(iframe);
  }
}

/**
 * 业务方法，场景说明，返回数组中数组最大的指定项
 * @param {Array} data 对象数组
 * @param {String} targetFieldsName 对象中的排序字段名
 * @param {Boolean} isDescend 默认降序排列
 * @param {Number} targetCount 返回的项数
 * @returns {Array} 指定的数组项
 */
export function targetMaxItem({
  data,
  targetFieldsName = 'data',
  isDescend = true, // 默认降序排列
  targetCount = 1,
  isEffective = true
}) {
  const sortData: any = data.sort((a, b) => {
    const targetA = +a[targetFieldsName];
    const targetB = +b[targetFieldsName];

    let comparison = 0;
    if (targetA > targetB) {
      comparison = 1;
    } else if (targetA < targetB) {
      comparison = -1;
    }
    return comparison * (isDescend ? -1 : 1);
  });
  if (targetCount > sortData.length) {
    return sortData;
  }
  const result: any = [];
  for (let i = 0; i < targetCount; i++) {
    const targetItem = sortData[i];
    if (isEffective) {
      targetItem[targetFieldsName] && result.push(targetItem);
    } else {
      result.push(targetItem);
    }
  }
  return result;
}

/**
 * 判断是否展示图表
 * @param {Array} data 对象数组
 * @returns {Boolean} 是否显示图表
 */
export function showTargetChart(data: Array<any>) {
  if (Array.isArray(data) && data.length) {
    if (data.every((item) => +item.data === 0)) {
      return false;
    }
    return true;
  }
  return false;
}

export const swipeDirection = (x1: number, x2: number, y1: number, y2: number) => {
  // return Math.abs(x1 - x2) >= Math.abs(y1 - y2) ? (x1 - x2 > 0 ? 'Left' : 'Right') : (y1 - y2 > 30 ? 'Up' : 'Down');
  if (Math.abs(x1 - x2) >= Math.abs(y1 - y2) && Math.abs(y1 - y2) < 25) { // x轴的偏移量（左右滑）
    if (Math.abs(x1 - x2) >= 15) {
      return x1 - x2 > 0 ? 'Left' : 'Right';
    }
    return 'x轴偏移量不足';
  }
  return y1 - y2 > 0 ? 'Up' : 'Down';
};

// 移动端手势
export const gestureTouch = (el: any, fn: Function) => {
  let startPoint: any = {};
  el.addEventListener('touchstart', (e: any) => {
    // e.preventDefault();
    startPoint = {
      x: e.changedTouches[0].pageX,
      y: e.changedTouches[0].pageY
    };
  });
  el.addEventListener('touchend', (e: any) => {
    const nowPoint = {
      x: e.changedTouches[0].pageX,
      y: e.changedTouches[0].pageY
    };
    // if (Math.abs(nowPoint.x - startPoint.x) > 10 && Math.abs(nowPoint.y - startPoint.y) > 10) {
    if (Math.abs(nowPoint.x - startPoint.x) > 50 || Math.abs(nowPoint.y - startPoint.y) > 50) {
      e.direction = swipeDirection(startPoint.x, nowPoint.x, startPoint.y, nowPoint.y);
      fn && fn.call(el, e);
    }
  });
};

/**
 * 针对场地图片的后缀处理
 * @param {String} url 场地图片url
 * @param {String} suffix 需要添加的后缀名
 * @returns {String} 返回能正常显示的图片url
 */
export function processingImgSuffix(url: string, suffix: string) {
  let needAddSuffix = false;
  const len = locationImageDomain.length;
  for (let i = 0; i < len; i++) {
    if (url.includes(locationImageDomain[i])) {
      needAddSuffix = true;
      break;
    }
  }
  if (needAddSuffix) {
    return `${url}${suffix}`;
  }
  return url;
}

/**
 * 针对接口返回 id name类型的数据转为antd mobile需要的label value
 * @param {Array} data 接口返回的数据
 * @param {Array} labelName 要替换的目标字段
 * @param {Array} valueName 要替换的目标字段值
 * @param {Array} targetLabelName 替换后的目标字段
 * @param {Array} targetValueName 替换后的目标字段值
 * @returns {Array} 替换后的数据
 */
export function replaceTargetFields(
  data: Array<any>,
  labelName = 'name',
  valueName = 'id',
  targetLabelName = 'label',
  targetValueName = 'value'
) {
  const labelNameReg = new RegExp(labelName, 'g');
  const valueNameReg = new RegExp(valueName, 'g');
  let jsonData = JSON.stringify(data);
  jsonData = jsonData.replace(labelNameReg, targetLabelName).replace(valueNameReg, targetValueName);
  return JSON.parse(jsonData);
}


/**
 * 过滤个别特殊省份
 * @param {Array} data 省市区
 * @param {Boolean} filtersAutonomy 是否过滤一国两制的省份（香港、澳门、台湾省）
 * @returns {Array} 过滤后的省市区
 */
export function filtersAutonomy(data: Array<any>, filtersAutonomy = true) {
  const autonomyProvinceName = ['香港', '澳门', '台湾'];
  if (filtersAutonomy) { // 过滤特殊的几个省份
    return data.filter((optionItem: any) => (!autonomyProvinceName.find((nameItem) => optionItem.label.indexOf(nameItem) > -1)));
  }
  return data;
}

/**
 * 处理数值的显示
 * @param { String | Number} num 传入的数值
 * @param { Boolean } automatic 是否自动根据小数的位数保留
 * @param { Number | Boolean } dicimal 含小数时截取n位小数，不需要时传false
 * @returns 整数时仍旧返回整数；小数时截取n位小数
 * 例如
 * fixNumber('1') => 1 // 正整数
 * fixNumber(1.223) => '1.22'
 * fixNumber('1.2') => 1.2
 * fixNumber('1.226' || 1.226, false) => '1.23'
 * fixNumber('1.226' || 1.226, false, 4) => '1.2260'
 * fixNumber(1.226, true, false) => 1.226
 */
export function fixNumber(num: number | string, automatic = true, dicimal: number | boolean = 2): string | number {
  if (+num) {
    if (Number.isInteger(+num)) return +num; // 正整数
    const decimalPointRight = num.toString().split('.')[1]; // 小数点右边的字符串
    const len = decimalPointRight.length; // 小数点有几位
    if (automatic) { // 自动处理小数点的显示
      if (dicimal) {
        if (len <= (dicimal as number)) return +num; // 小数有几位就保留几位
        return (+num as number).toFixed(dicimal as number); // 统一保留dicimal的位数
      }
      return +num; // 小数有几位就保留几位
    }
    return (+num as number).toFixed(dicimal as number); // 统一保留dicimal的位数
  }
  return num;
}

/**
 * 将数字转成k、w结尾
 * @param num 值
 * @param digit 小数位数 默认保留 1 位
 * @returns 1000转化为1k，10000转为1w,10000000转为1kw
 */
export function formatNumber(num: number, digit = 1) {
  // return num >= 1e3 && num < 1e4 ? (num / 1e3).toFixed(1) + 'k' : num >= 1e4 ? (num / 1e4).toFixed(1) + 'w' : num;
  if (num >= 1e3 && num < 1e4) {
    return (num / 1e3).toFixed(digit) + 'k';
  } else if (num >= 1e4 && num < 1e8) {
    return (num / 1e4).toFixed(digit) + 'w';
  } else if (num >= 1e8) {
    return (num / 1e8).toFixed(digit) + '亿';
  } else {
    return num;
  }
}

/** 省市区转换处理 去掉省市区*/
export function removeLocationInfo(address: string): string {
  const patterns = [
    /^(.*?(?:省|自治区|北京市|天津市|上海市|重庆市))/,
    /^(.*?(?:市|自治州|地区|盟))/,
    /^(.*?(?:区|县|县级市|旗))/,
  ];

  let trimmedAddress = address;

  for (const pattern of patterns) {
    const match = trimmedAddress.match(pattern);
    if (match && match[1]) {
      trimmedAddress = trimmedAddress.replace(match[1], '').trim();
    }
  }

  return trimmedAddress || address;
}

/**
 *  区分是浏览器打开还是app内打开（利用useragent,android约定返回字符串location-android,ios返回location-ios）
 */
export function isApp() {
  const UA = navigator.userAgent.toLowerCase(); // crm-android ,crm-ios
  if (UA === 'location-android') {
    return true;
  } else if (UA === 'location-ios') {
    return true;
  } else {
    return false;
  }
};

/**
 *  是否android app
 */
export function isAppAndroid() {
  const UA = navigator.userAgent.toLowerCase(); // crm-android ,crm-ios
  return UA === 'location-android';
};

/**
 * @description 获取指定日期为周几
 * @param date
 * @return 周几
 */
export const getDateWeek = (date) => {
  const newDate = !date ? new Date() : new Date(typeof date === 'string' ? date.replace(/-|\./g, '/') : date);
  const week = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  return week[newDate.getDay()];
};
// 白鹰摄像头解密key
export const cameraDecodeAESKey = 'd2417aa05995b246';

/**
 * @description 解密AES--目前使用场景是扫码解密白鹰摄像头
 * @params code
 * @return 解密后的数据
 */
export function decodeAES(code: string, key:string) {

  // 将密钥转换为WordArray
  const keyBytes = CryptoJS.enc.Utf8.parse(key);

  // 将加密后的数据转换为WordArray
  const encryptedBytes = CryptoJS.enc.Hex.parse(code);

  // 解密
  const decrypted = CryptoJS.AES.decrypt({
    ciphertext: encryptedBytes
  }, keyBytes, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });

  // 将解密后的数据转换为UTF-8字符串
  const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

  // console.log(decryptedText); // 输出解密后的数据

  return decryptedText;
}


const targetVersion = 2.38; // 版本号2.33.0
/**
 * @description App端版本大于2.38.0 时安卓开启定位权限开启提示
 */
export function targetVersionShowPermissionHint() {
  if (!isApp()) { // 非app环境
    throw new Error('非App环境内');
  }
  return new Promise(async (resolve) => {
    // version可能是 2.11.11，也可能是 2.11.11beta
    const version: any = await getAppVersion();
    const numberVersion = parseFloat(version);
    resolve(numberVersion > targetVersion);
  });
}

/**
 * @description 判断app版本大于等于指定版本 例2.41
 */
export function laterThanTargetVersion(tarVersion) {
  if (!isApp()) { // 非app环境
    throw new Error('非App环境内');
  }
  return new Promise(async (resolve) => {
    // version可能是 2.11.11，也可能是 2.11.11beta
    const version: any = await getAppVersion();
    const numberVersion = parseFloat(version);
    resolve(numberVersion >= tarVersion);
  });
}

/**
 * 滚动到第一个必填未填项
 */
export function scrollToError(trueDocument = document) {
  setTimeout(() => {
    const errorLists = trueDocument?.querySelector('.adm-form-item-has-error');
    console.log('errorLists', errorLists);
    errorLists?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // errorLists && errorLists[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 0);
};

// 秒转时分秒
export const secondFormatTime = (seconds: number) => {
  if (!seconds) return '-';
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}小时${minutes}分钟`;
  } else if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}分钟`;
  } else {
    return `${seconds}秒`;
  }
};

/**
 * 是否在微信小程序内
 */
export const isWxMiniProgram = () => {
  const userAgent = navigator.userAgent;
  return /miniProgram/i.test(userAgent) && /micromessenger/i.test(userAgent);
};

/**
 * @description 判断是否需要继续解码（url参数中经过encodeURIComponent的参数会在分享过程中被二次编码）
 * @param {string} value 需要解码的值
 * @returns {string} 解码后的值
 */
export function isDecoded(value: string) {
  try {
    // 尝试解码值
    const decodedValue = decodeURIComponent(value);
    // 如果解码后的值与原值相同，则不需要再解码
    return decodedValue === value;
  } catch (e) {
    // 如果解码失败，则该值可能已经是解码过的
    return false;
  }
}

// 处理拓店任务别名
export const globalExpansionTaskAlias = (globalAliasItemVOs) => {
  if (isArray(globalAliasItemVOs) && globalAliasItemVOs.length) {
    const target = globalAliasItemVOs.find((item: any) => item.code === 'standardTask');
    if (isNotEmptyAny(target)) return target.alias;
  }
  return '';
};

/**
 * 是否是飞书环境内
 */
export const isLarkEnv = () => {
  return window.h5sdk && window.tt;
};

// 是否是钉钉移动端
export const isDDAppEnv = () => {
  const platform = dd?.env?.platform;
  return platform === 'android' || platform === 'ios';
};

/**
 * @description 判断是否需要继续解码（url参数中经过encodeURIComponent的参数会在分享过程中被二次编码），如果需要就继续解码，直到解码成功
 * @param {string} value 需要解码的值
 * @returns {string} 解码后的值
 */
export function recursiveDecodeURIComponent(encodedStr: string): any {
  try {
    // 尝试进行一次解码
    const decodedStr = decodeURIComponent(encodedStr);
    // 如果解码后的字符串和原字符串相同，说明已经无法再解码
    if (decodedStr === encodedStr) {
      return decodedStr;
    }
    // 否则，继续递归解码
    return recursiveDecodeURIComponent(decodedStr);
  } catch (error) {
    // 如果解码过程中出现错误，说明已经是最终解码结果
    return encodedStr;
  }
}

/**
 * 判断是否是ios设备
 */
export function isIOS() {
  const userAgent = navigator.userAgent;
  const iosRegex = /(iPhone|iPad|iPod).*OS (\d+)_(\d+)/i;
  return iosRegex.test(userAgent);
}

/**
 * ios 手机的默认状态栏高度
 */
export function defaultStatusBarHeightIOS() {
  if (isApp()) { // App环境内
    return '48px';
  }
  if (isLarkEnv() && isIOS()) { // 飞书的ios环境内
    return '44px';
  }
  return 0;
}

/**
 * 手机的默认状态栏高度（钉钉环境内）
 */
export function defaultStatusBarHeightDD() {
  if (isApp()) { // App环境内
    return '48px';
  }
  if (isDDAppEnv() && isIOS()) { // 钉钉的ios环境内
    return '44px';
  }
  return 0;
}

/**
 * 接口返回的日期格式转换为前端显示的日期格式 2020-12-12 -> 2020.12.12
 */
export function dateFormatShow(date: string) {
  if (!date || typeof date !== 'string') return;
  return date.replace(/-/gi, '.');
}

/**
 * @description 跳转页面
 * @param {string} url 跳转的url
 * @param {string} type 跳转的类型
 */
export function jumpWrap(url: string, type = 'reactH5') {
  if (isApp()) {
    routePage({ type, path: url });
    return;
  } else {
    dispatchNavigate(url);
  }
}


/**
 * @param substring token值
 * @returns {} 存放在token中的数据，比如：
 * substring为: "id=2 employeeId=358574 tenantId=117663 clientKey=1000114 share=false createAt=2025-07-22 14:03:58"，
 * 解析后会变成对应的键值对对象：
 * {
 *  employeeId: 358574,
 *  ...
 * }
 */
export function parseTokenSubString(substring: string) {
  // 利用正则表达式找出所有的键值对
  const regex = /(\w+)=([^=\s]+|".*?"|'.*?'|true|false)/g;
  const result: any = {};
  let match;

  while ((match = regex.exec(substring)) !== null) {
    const key = match[1];
    let value: any = match[2];

    // 处理布尔值
    if (value === 'true' || value === 'false') {
      value = value === 'true';
    } else if (!isNaN(value) && value.trim() !== '') { // 处理数值
      value = Number(value);
    } else if ((value.startsWith('"') && value.endsWith('"')) ||
               (value.startsWith("'") && value.endsWith("'"))) { // 处理带引号的字符串
      value = value.slice(1, -1);
    }

    result[key] = value;
  }

  return result;
}

/**
 * 利用token解析出员工id和租户id，然后写入到cookie，确保埋点上报时有对应的参数
 */
export function makeUseOfTokenGetData() {
  const targetEmployeeId = getCookie('EMPLOYEE_ID');
  const targetTenantId = getCookie('TENANT_ID');
  let isShareUrl = false; // 如果shareId参数存在，就代表是处于分享页
  try {
    if (matchQuery(window.location.href, 'shareId')) {
      isShareUrl = true;
    } else if (window.parent?.location?.href && matchQuery(window.parent.location.href, 'shareId')) { // 父窗口嵌套
      isShareUrl = true;
    }
  } catch (err) {}
  const token = isShareUrl ? getCookie('flow_share_token') : getCookie('flow_token');
  // 处理H5环境下，会话cookie存储的员工id和租户id因为关闭会话导致的丢失问题
  if (!isApp() && token && (!targetEmployeeId || !targetTenantId)) {
    const decodedVal = jwtDecode(token);
    const { sub } = decodedVal || {};
    const targetSubObj = sub ? parseTokenSubString(sub) : {};
    const { employeeId, tenantId } = targetSubObj || {};
    if (employeeId) {
      setCookie('EMPLOYEE_ID', employeeId);
    }
    if (tenantId) {
      setCookie('TENANT_ID', tenantId);
    }
  }
}

/**
 * [showInvalidFieldMsg 解析el-form校验未通过的字段信息]
 * @param  {[type]} object    未通过校验的字段
 * @param  {[type]} maxLength 提示内容的最大个数，默认 -1无限制
 * @return {[type]}        [description]
 */
export function showInvalidFieldMsg(errorFields, maxLength = -1) {
  if (!Array.isArray(errorFields) || !errorFields.length) {
    return null;
  }
  const arr: any = [];
  errorFields.forEach(item => {
    const data = parseArrayToString(item.errors);
    if (data) {
      arr.push(data);
    }
  });

  const messages = maxLength === -1 ? arr : arr.slice(0, maxLength);
  Toast.show(parseArrayToString(messages));
  // window.microMessage.warning(parseArrayToString(messages));

  return messages;
}

/**
 * 退出登录
 */
export function handleLogout() {
  return new Promise(async () => {
    const bridge = await bridgeLogout(); // 通知app清除设备id，不再接收推送消息
    // 判断是否有uims_token,有的话v1，否则v2
    // const isV2 = !!getStorage('uims_token') || !!getCookie('flow_token');
    const isQyWx = window.navigator.userAgent.includes('wxwork'); // 判断是否在企业微信环境中
    logout({ device_token: bridge }).then(() => {
      setCookie('userInfo', ''); // 没看到有赋值的地方
      setCookie('flow_token', '');
      setCookie('flow_share_token', '');
      setCookie('tenantId', '');
      setCookie('employeeId', '');
      setCookie('TENANT_ID', '');
      setCookie('EMPLOYEE_ID', '');
      localStorage.clear();
      sessionStorage.clear();
      // 如果在企业微信浏览器内退出则跳转到console-pc响应式的登录页
      if (isQyWx) {
        // window.location.href = process.env.CONSOLE_PC_URL + '';
        dispatchNavigate(buildLoginPath());
      } else {
        dispatchNavigate(buildLoginPath());
      }
    }).catch(({ message }) => {
      message && Toast.show(message);
    });
  });
}

export function qmoorChat() {
  const user_id = getStorage('userInfo') ? getStorage('userInfo').user_id : ''; // (当前登录的用户id)
  if (isApp()) {
    jumpWechatService('https://webchat.7moor.com/wapchat.html?accessId=fa225590-26bd-11ea-a403-31c5672534d7&fromUrl=locationweb2b.lanhanba.net&urlTitle=location&clientId=location' + user_id);
  } else {
    window.location.href = `tel:${SERVICE_TEL}`;
  }
}

/**
 * 利用token解析出token中存储的数据，目前用在App环境内切换租户/环境后，本地cookie中的TENANT_ID和EMPLOYEE_ID未更新的场景。
 */
export async function parseTokenGetData() {
  // UIMS_TOKEN代表原生App写入的cookie的token值
  let headersToken = getCookie('UIMS_TOKEN');
  if (isApp()) { // App环境内不会有flow_token
    if (headersToken) { // App写入到webview里面的混淆过的token
      headersToken = headersToken.substring(2, headersToken.length - 8);
    } else { // App没写入到webview中,利用桥接事件获取
      const { uimsToken }: any = await getAfferentToken();
      headersToken = uimsToken;
    }
  }
  // 不考虑H5开发环境，如果需要就取消该段注释
  // if (process.env.NODE_ENV === 'development') {
  //   headersToken = getCookie('flow_token');
  // }
  if (!headersToken) return null;
  const decodedVal = jwtDecode(headersToken);
  const { sub } = decodedVal || {};
  const targetSubObj = sub ? parseTokenSubString(sub) : {};
  return targetSubObj || {};
}

// 解析 getContainer，来自 node_modules/antd-mobile/es/utils/get-container.js
export function resolveContainer(getContainer) {
  const container = typeof getContainer === 'function' ? getContainer() : getContainer;
  return container || document.body;
}

/**
 * 截取字符串前7位并拼接"..."的方法
 * @description 用于列表顶部筛选显示选中文本
 * @demo

 */
export function truncateString(str, limit = 7) {
  return isNotEmpty(str) && String(str).length > limit ? str.substring(0, limit) + '...' : str;
}

/**
 * @description 从完整地址中去掉省市区信息
 * @param address 完整地址
 * @param pcdNames 字符串数组，包含省市区名称，个数不限制，包含的单个字符串为空时无实际效果
 * @return 去掉省市区后的地址
 */
export function removePcdFromAddress(address: string, pcdNames: string[]) {
  let res = address || '';
  Array.isArray(pcdNames) && pcdNames.forEach((name: string) => {
    if (name && res.startsWith(name)) {
      res = res.slice(name.length);
    }
  });
  return res;
}

// 时间维度 1:天 2:周 3:月 4:季 5:年
enum TimeDimension {
  Day = 1, // 审批中
  Week,
  Month,
  Season,
  Year,
};
export const TimeDimensionMap = new Map([
  [TimeDimension.Day, '天'],
  [TimeDimension.Week, '周'],
  [TimeDimension.Month, '月'],
  [TimeDimension.Season, '季'],
  [TimeDimension.Year, '年'],
]);

/**
 * @description 格式化预测营业额相关数据
 * @param data
 * @returns {val, unit, title}
 */
export const formatPredictSale = (timeDimension: any, salesAmountPredict: number) => {
  const getTargetNum = (num: number) => {
    return (+floorKeep(num, 10000.0, 4, 2) as number).toFixed(2);
  };

  const title = `预测${TimeDimensionMap.get(timeDimension) || ''}营业额`;
  const canShowValue = isNotEmpty(salesAmountPredict);
  if (canShowValue) {
    let unit = '元';
    let val = `${salesAmountPredict}`;
    if (salesAmountPredict >= 10000) {
      unit = '万元';
      val = getTargetNum(salesAmountPredict);
    }
    return {
      val,
      unit,
      title,
    };
  }
  return {
    val: '',
    unit: '',
    title,
  };
};


// 从七牛云获取一个授权token或者从oss获取一个占位token（无意义）
export function getUploadCustomToken(params: any) {
  if (isOss) {
    return new Promise(resolve => {
      // oss模式不需要token，占位就行
      resolve({
        data: {
          token: 'tokenPlaceHolder'
        }
      });
    });
  } else {
    /**
     * 七牛token
     * https://yapi.lanhanba.com/project/297/interface/api/34045
     */
    /**
     * 分享页用的七牛token
     * https://yapi.lanhanba.com/project/297/interface/api/
     * 上面的/qiniu/token服务端把改成了需要登录才能使用，所以分享场景时使用该新接口
     */
    return get('/v2/qiniu/token', { ...params }, { needCancel: false });
  }
}

export const wxWorkEnable = () => isWxWorkBrowser();

/**
 *  是否企业微信内置浏览器
 */
export const isWxWorkBrowser = () => {
  const ua = navigator.userAgent;
  return /MicroMessenger/i.test(ua) && /WxWork/i.test(ua); // 微信且企业微信
};


/**
 * 平展树状结构数组
 * @param {Array} arr - 嵌套数组
 * @param {Object} options - 配置选项
 * @param {string} options.childrenKey - 子节点键名，默认 'children'
 * @param {boolean} options.includeChildren - 是否包含 children，默认 false
 * @param {Function} options.transform - 转换函数
 * @param {Function} options.filter - 过滤函数
 * @returns {Array} 平铺后的数组
 */
export function flattenTree(arr, options: {
  // labelKey?: string,
  valueKey?: string,
  childrenKey?: string,
  transform?: (item: any, parent?: any) => any,
  filter?: (item: any, parent?: any) => boolean,
} = {}) {
  const _options: any = Object.assign({
    // labelKey = 'label',
    valueKey: 'value',
    childrenKey: 'children',
    transform: null,
    filter: null
  }, options || {});
  const {
    // labelKey = 'label',
    valueKey = 'value',
    childrenKey = 'children',
    transform = null,
    filter = null
  } = _options;

  if (!Array.isArray(arr)) return {};

  const result: any = {};

  const traverse = (nodes, parent = null) => {
    nodes.forEach(node => {
      if (!node || typeof node !== 'object') return;

      // 准备要添加的项
      let itemToAdd = { ...node };


      // 应用转换函数
      if (typeof transform === 'function') {
        itemToAdd = transform(itemToAdd, parent);
      }

      // 检查是否通过过滤
      const shouldInclude = typeof filter === 'function'
        ? filter(itemToAdd, parent)
        : true;

      if (shouldInclude) {
        result[itemToAdd[valueKey]] = itemToAdd;
      }

      // 递归处理子节点
      const children = node[childrenKey];
      if (Array.isArray(children) && children.length) {
        traverse(children, node);
      }
    });
  };

  traverse(deepCopy(arr));
  return result;
}
